import { useEffect, useState, type ChangeEvent } from 'react'
import { useRepos } from '../../state/repos'
import type { ListItem } from '../../db/repositories'
import type { BookContent } from '../../types/records'
import { importBookIntoRepos, type ImportProgress } from '../../import/importBook'

type ImportStatus =
  | { kind: 'idle' }
  | { kind: 'running'; progress: ImportProgress }
  | { kind: 'success'; garmentsImported: number }
  | { kind: 'error'; message: string }

export function BookTab() {
  const { books, garments } = useRepos()
  const [items, setItems] = useState<ListItem<BookContent>[]>([])
  const [garmentsCount, setGarmentsCount] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [indexFile, setIndexFile] = useState<File | null>(null)
  const [tablesFile, setTablesFile] = useState<File | null>(null)
  const [garmentsFile, setGarmentsFile] = useState<File | null>(null)
  const [status, setStatus] = useState<ImportStatus>({ kind: 'idle' })

  async function reload() {
    const [bookList, garmentList] = await Promise.all([books.list(), garments.list()])
    setItems(bookList)
    setGarmentsCount(garmentList.length)
    setLoaded(true)
  }

  useEffect(() => {
    void reload()
  }, [books, garments])

  function pick(setter: (f: File | null) => void) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      setter(e.target.files?.[0] ?? null)
      setStatus({ kind: 'idle' })
    }
  }

  async function importBundled() {
    setStatus({
      kind: 'running',
      progress: { phase: 'parsing', garmentsTotal: 0, garmentsSaved: 0 },
    })
    try {
      const base = `${import.meta.env.BASE_URL}data`
      const [idx, tbl, grm] = await Promise.all([
        fetch(`${base}/book-index.json`),
        fetch(`${base}/book-tables.json`),
        fetch(`${base}/book-garments.json`),
      ])
      if (!idx.ok || !tbl.ok || !grm.ok) {
        throw new Error('Dataset incluso non raggiungibile (riprova online)')
      }
      const mkFile = async (r: Response, name: string) =>
        new File([await r.blob()], name, { type: 'application/json' })
      const files = {
        index: await mkFile(idx, 'book-index.json'),
        tables: await mkFile(tbl, 'book-tables.json'),
        garments: await mkFile(grm, 'book-garments.json'),
      }
      const result = await importBookIntoRepos(files, { books, garments }, p =>
        setStatus({ kind: 'running', progress: p }),
      )
      setStatus({ kind: 'success', garmentsImported: result.garmentsImported })
      setShowImport(false)
      await reload()
    } catch (e) {
      setStatus({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Errore sconosciuto',
      })
    }
  }

  async function runImport() {
    if (!indexFile || !tablesFile || !garmentsFile) {
      setStatus({ kind: 'error', message: 'Servono tutti e 3 i file' })
      return
    }
    setStatus({
      kind: 'running',
      progress: { phase: 'parsing', garmentsTotal: 0, garmentsSaved: 0 },
    })
    try {
      const result = await importBookIntoRepos(
        { index: indexFile, tables: tablesFile, garments: garmentsFile },
        { books, garments },
        progress => setStatus({ kind: 'running', progress }),
      )
      setStatus({ kind: 'success', garmentsImported: result.garmentsImported })
      setShowImport(false)
      setIndexFile(null)
      setTablesFile(null)
      setGarmentsFile(null)
      await reload()
    } catch (e) {
      setStatus({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Errore sconosciuto',
      })
    }
  }

  if (!loaded) return <p className="muted">Caricamento…</p>

  const importing = status.kind === 'running'
  const allFilesPicked = indexFile && tablesFile && garmentsFile

  return (
    <section className="tab-section">
      <h3>Libro metodologia</h3>

      {items.length === 0 && !showImport && (
        <div className="empty-state">
          <p>Nessun libro caricato.</p>
          <p className="hint">
            Carica i 3 file JSON prodotti dagli script di estrazione
            (book-index.json, book-tables.json, book-garments.json) per
            popolare l'app con il libro completo.
          </p>
          <div className="row" style={{ justifyContent: 'center', gap: '0.6rem' }}>
            <button onClick={importBundled} disabled={importing}>
              {importing ? 'Caricamento…' : 'Carica dataset incluso'}
            </button>
            <button className="secondary" onClick={() => setShowImport(true)}>
              Importa da file
            </button>
          </div>
        </div>
      )}

      {items.length > 0 && !showImport && (
        <>
          <ul className="list">
            {items.map(item => (
              <li key={item.id}>
                <strong>{item.content.title}</strong>
                <span className="muted">
                  {item.content.author && `${item.content.author} — `}
                  {item.content.totalPages} pagine
                </span>
                <span className="muted">
                  {item.content.measurementsBase.length} misure base ·{' '}
                  {item.content.sizesWomen.sizes.length} taglie donna ·{' '}
                  {item.content.sizesMen.sizes.length} taglie uomo ·{' '}
                  {garmentsCount} capi
                </span>
                <span className="muted">
                  Importato il{' '}
                  {new Date(item.content.importedAt).toLocaleDateString('it-IT')}
                </span>
              </li>
            ))}
          </ul>
          <div className="row" style={{ marginTop: '1rem' }}>
            <button onClick={importBundled} disabled={importing}>
              {importing ? 'Caricamento…' : 'Aggiorna al dataset incluso'}
            </button>
            <button className="secondary" onClick={() => setShowImport(true)}>
              Sostituisci da file
            </button>
          </div>
        </>
      )}

      {showImport && (
        <div className="import-panel">
          <h4>Importa libro</h4>
          {items.length > 0 && (
            <div className="status status-err" style={{ marginBottom: '1rem' }}>
              Attenzione: l'import sostituirà il libro corrente e tutti i {garmentsCount} capi.
              I progetti e i pattern già creati rimangono intatti.
            </div>
          )}

          <FilePicker
            label="① book-index.json"
            file={indexFile}
            onChange={pick(setIndexFile)}
            disabled={importing}
          />
          <FilePicker
            label="② book-tables.json"
            file={tablesFile}
            onChange={pick(setTablesFile)}
            disabled={importing}
          />
          <FilePicker
            label="③ book-garments.json"
            file={garmentsFile}
            onChange={pick(setGarmentsFile)}
            disabled={importing}
          />

          <div className="row" style={{ marginTop: '1rem' }}>
            <button onClick={runImport} disabled={!allFilesPicked || importing}>
              {importing ? 'Importazione…' : 'Importa'}
            </button>
            <button
              className="secondary"
              onClick={() => {
                setShowImport(false)
                setStatus({ kind: 'idle' })
              }}
              disabled={importing}
            >
              Annulla
            </button>
          </div>

          {status.kind === 'running' && (
            <div className="status">
              {progressLabel(status.progress)}
            </div>
          )}
          {status.kind === 'success' && (
            <div className="status status-ok">
              Import completato. {status.garmentsImported} capi caricati.
            </div>
          )}
          {status.kind === 'error' && (
            <div className="status status-err">{status.message}</div>
          )}

          <p className="hint">
            I 3 file vengono parsati, validati e cifrati con la master password
            prima di essere salvati nel browser. I file originali sul disco
            non vengono toccati.
          </p>
        </div>
      )}
    </section>
  )
}

function FilePicker({
  label,
  file,
  onChange,
  disabled,
}: {
  label: string
  file: File | null
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  disabled: boolean
}) {
  return (
    <div className="file-picker">
      <label>
        <span>{label}</span>
        <input
          type="file"
          accept="application/json,.json"
          onChange={onChange}
          disabled={disabled}
        />
      </label>
      <span className={file ? 'file-status ok' : 'file-status none'}>
        {file ? `✓ ${file.name} (${(file.size / 1024).toFixed(0)} KB)` : 'non scelto'}
      </span>
    </div>
  )
}

function progressLabel(p: ImportProgress): string {
  switch (p.phase) {
    case 'parsing':
      return 'Parsing dei file JSON…'
    case 'saving-book':
      return 'Salvataggio libro cifrato…'
    case 'saving-garments':
      return `Salvataggio capi cifrati: ${p.garmentsSaved} / ${p.garmentsTotal}…`
    case 'done':
      return 'Import completato.'
  }
}
